import type { MaybeRefOrGetter } from "vue";
import { ablySdkLogPrefix } from "./use-ably-sdk";
import type Ably from "ably";
import type { MessageReaderHandle } from "./ably-channel-registry";

export interface IPresenceData<T> {
  data: T;
  metadata: IPresenceMetadata;
}

export interface IPresenceMetadata {
  /**
   * Placeholder so TypeScript enforce the presence of metadata.
   */
  ___?: boolean;
}

export interface AblyChannelSyntheticOptions {
  rewind: MaybeRefOrGetter<number>;
  topic?: MaybeRefOrGetter<string>;
}

export interface AblyChannelSyntheticOptionsDefaults extends AblyChannelSyntheticOptions {
  topic: MaybeRefOrGetter<string>;
}

export const defaultAblyChannelTopic = "state";
export const withAblyChannelSyntheticDefaultOptions = (
  defaults: AblyChannelSyntheticOptions,
  options?: AblyChannelSyntheticOptions,
): AblyChannelSyntheticOptionsDefaults => ({
  rewind: options?.rewind ?? defaults.rewind,
  topic: options?.topic ?? defaultAblyChannelTopic,
});

async function* iterateAblyHistoryResult(
  baseResult: Ably.PaginatedResult<Ably.Message>,
  rewindUntil: number,
): AsyncGenerator<Ably.Message> {
  let result = baseResult;
  let items: Ably.Message[] | null = [...result.items];

  while (rewindUntil-- > 0) {
    if (items.length === 0 && result.hasNext()) {
      const nextResult = await result.next();

      if (nextResult) {
        items = [...nextResult.items];
        result = nextResult;
      } else {
        console.log(
          ablySdkLogPrefix + "Attempted to fetch next page but no result",
        );
        return;
      }
    }

    const item = items.pop();
    if (item) yield item;
  }

  console.log(ablySdkLogPrefix + "No more messages available in stream");
}

export async function rewindChannel(
  handle: MessageReaderHandle,
  rewind: number,
  callback: (message: Ably.Message) => void,
) {
  try {
    const history = await handle.history({
      direction: "backwards",
      limit: Math.min(rewind, 20),
    });

    if (history.items.length === 0) {
      if (import.meta.env.DEV) {
        console.log(
          ablySdkLogPrefix + "No history found for channel",
          handle.name,
        );
      }
    } else {
      for await (const message of iterateAblyHistoryResult(history, rewind)) {
        callback(message);
      }
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(
        ablySdkLogPrefix + "Error fetching history for channel",
        handle.name,
        error,
      );
    }
  }
}
